import React, { forwardRef, RefObject, useMemo } from 'react';

import {
  FormControl,
  FormControlProps,
  FormErrorMessage,
  Input as ChakraInput,
  InputAddonProps,
  InputGroup,
  InputLeftElement,
  InputProps as ChakraInputProps,
  InputRightAddon,
  InputRightElement,
  Text,
  useTheme,
} from '@chakra-ui/react';
import { MdCancel } from 'react-icons/md';

import { FormInputProps } from '../FormInput';
import FormLabel from '../FormLabel';

type InputProps = ChakraInputProps &
  FormInputProps & {
    clearFieldHandler?: () => void;
    error?: string | undefined;
    showClear?: boolean;
    containerProps?: FormControlProps;
    leftElement?: string | React.ReactNode;
    rightAddon?: string | React.ReactNode;
    hintText?: string | React.ReactNode;
    rightAddonProps?: InputAddonProps;
  };

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      clearFieldHandler,
      error,
      hasTooltip,
      label,
      showClear,
      tooltipText,
      variant = 'primary',
      containerProps,
      id,
      leftElement,
      rightAddon,
      rightAddonProps,
      hintText,
      isInvalid,
      isDisabled,
      ...rest
    },
    ref,
  ) => {
    const inputId = useMemo(() => id || `${Date.now()}-${Math.random()}`, [id]);
    const theme = useTheme();

    return (
      <FormControl
        id={inputId}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        isReadOnly={variant === 'readonly'}
        {...containerProps}
      >
        {label && (
          <FormLabel
            hasTooltip={hasTooltip}
            id={inputId}
            label={label}
            tooltipText={tooltipText}
          />
        )}

        {hintText && (
          <Text color='app.widgetGray' fontSize='sm' fontWeight='500' mb='xs'>
            {hintText}
          </Text>
        )}

        <InputGroup>
          {leftElement ? (
            <InputLeftElement
              color='gray.300'
              fontSize='1.2em'
              pointerEvents='none'
            >
              {leftElement}
            </InputLeftElement>
          ) : null}
          <ChakraInput
            id={inputId}
            isDisabled={isDisabled}
            ref={ref as RefObject<HTMLInputElement>}
            variant={variant}
            {...rest}
          />
          {showClear && !isDisabled && (
            <InputRightElement>
              <MdCancel
                color={theme.colors.app.textGray}
                cursor='pointer'
                onClick={clearFieldHandler}
                size='1.375rem'
              />
            </InputRightElement>
          )}
          {rightAddon ? (
            <InputRightAddon {...rightAddonProps}>{rightAddon}</InputRightAddon>
          ) : null}
        </InputGroup>

        <FormErrorMessage>{error}</FormErrorMessage>
      </FormControl>
    );
  },
);

Input.displayName = 'Input';

export default Input;
